import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  Settings as SettingsIcon, 
  Globe,
  Tag,
  RefreshCw,
  Loader2,
  Edit,
  Key,
  Copy,
  Trash2,
  Eye,
  EyeOff,
  Code,
  Webhook,
  ExternalLink,
  CheckCircle2,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Settings() {
  const [policies, setPolicies] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [apiDocs, setApiDocs] = useState(null);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showNewKey, setShowNewKey] = useState(false);
  
  // Referrer Whitelist state
  const [referrerWhitelist, setReferrerWhitelist] = useState([]);
  const [referrerDialogOpen, setReferrerDialogOpen] = useState(false);
  const [editingReferrer, setEditingReferrer] = useState(null);
  const [referrerForm, setReferrerForm] = useState({
    referrer_id: '',
    display_name: '',
    description: ''
  });
  const [submittingReferrer, setSubmittingReferrer] = useState(false);
  
  // Policy Dialog
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    country_code: '',
    country_name: '',
    accepted_document_types: ['national_id', 'passport'],
    min_quality_threshold: 0.7,
    max_retries: 3,
    auto_approve_threshold: 85,
    auto_reject_threshold: 20,
    sla_hours: 24
  });
  const [submittingPolicy, setSubmittingPolicy] = useState(false);
  
  // Reason Code Dialog
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonForm, setReasonForm] = useState({
    code: '',
    description: '',
    category: 'approval'
  });
  const [submittingReason, setSubmittingReason] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [policiesRes, reasonsRes, apiKeysRes, apiDocsRes, referrerRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/policies`),
        axios.get(`${API_URL}/api/admin/reason-codes`),
        axios.get(`${API_URL}/api/admin/api-keys`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/api-docs`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/referrer-whitelist`).catch(() => ({ data: [] }))
      ]);
      setPolicies(policiesRes.data);
      setReasonCodes(reasonsRes.data);
      setApiKeys(apiKeysRes.data || []);
      setApiDocs(apiDocsRes.data);
      setReferrerWhitelist(referrerRes.data || []);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Referrer Whitelist handlers
  const handleSaveReferrer = async () => {
    if (!referrerForm.referrer_id || !referrerForm.display_name) {
      toast.error('Please fill in referrer ID and display name');
      return;
    }

    try {
      setSubmittingReferrer(true);
      
      if (editingReferrer) {
        await axios.put(`${API_URL}/api/admin/referrer-whitelist/${editingReferrer.referrer_id}`, referrerForm);
        toast.success('Referrer updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/referrer-whitelist`, referrerForm);
        toast.success('Referrer added to whitelist');
      }
      
      setReferrerDialogOpen(false);
      setEditingReferrer(null);
      setReferrerForm({ referrer_id: '', display_name: '', description: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to save referrer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save referrer');
    } finally {
      setSubmittingReferrer(false);
    }
  };

  const handleEditReferrer = (referrer) => {
    setEditingReferrer(referrer);
    setReferrerForm({
      referrer_id: referrer.referrer_id,
      display_name: referrer.display_name,
      description: referrer.description || ''
    });
    setReferrerDialogOpen(true);
  };

  const handleDeleteReferrer = async (referrerId) => {
    if (!window.confirm('Are you sure you want to remove this referrer from the whitelist?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin/referrer-whitelist/${referrerId}`);
      toast.success('Referrer removed from whitelist');
      fetchData();
    } catch (error) {
      console.error('Failed to delete referrer:', error);
      toast.error('Failed to remove referrer');
    }
  };

  const handleToggleReferrer = async (referrerId) => {
    try {
      const response = await axios.patch(`${API_URL}/api/admin/referrer-whitelist/${referrerId}/toggle`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Failed to toggle referrer:', error);
      toast.error('Failed to toggle referrer status');
    }
  };

  const resetReferrerForm = () => {
    setEditingReferrer(null);
    setReferrerForm({ referrer_id: '', display_name: '', description: '' });
  };

  const handleCreatePolicy = async () => {
    if (!policyForm.country_code || !policyForm.country_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmittingPolicy(true);
      
      if (editingPolicy) {
        // Update existing policy
        await axios.put(`${API_URL}/api/admin/policies/${editingPolicy.country_code}`, policyForm);
        toast.success('Policy updated successfully');
      } else {
        // Create new policy
        await axios.post(`${API_URL}/api/admin/policies`, policyForm);
        toast.success('Policy created successfully');
      }
      
      setPolicyDialogOpen(false);
      setEditingPolicy(null);
      setPolicyForm({
        country_code: '',
        country_name: '',
        accepted_document_types: ['national_id', 'passport'],
        min_quality_threshold: 0.7,
        max_retries: 3,
        auto_approve_threshold: 85,
        auto_reject_threshold: 20,
        sla_hours: 24
      });
      fetchData();
    } catch (error) {
      console.error('Failed to save policy:', error);
      toast.error(editingPolicy ? 'Failed to update policy' : 'Failed to create policy');
    } finally {
      setSubmittingPolicy(false);
    }
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      country_code: policy.country_code,
      country_name: policy.country_name,
      accepted_document_types: policy.accepted_document_types || ['national_id', 'passport'],
      min_quality_threshold: policy.min_quality_threshold || 0.7,
      max_retries: policy.max_retries || 3,
      auto_approve_threshold: policy.auto_approve_threshold || 85,
      auto_reject_threshold: policy.auto_reject_threshold || 20,
      sla_hours: policy.sla_hours || 24
    });
    setPolicyDialogOpen(true);
  };

  const resetPolicyForm = () => {
    setEditingPolicy(null);
    setPolicyForm({
      country_code: '',
      country_name: '',
      accepted_document_types: ['national_id', 'passport'],
      min_quality_threshold: 0.7,
      max_retries: 3,
      auto_approve_threshold: 85,
      auto_reject_threshold: 20,
      sla_hours: 24
    });
  };

  const handleCreateReasonCode = async () => {
    if (!reasonForm.code || !reasonForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmittingReason(true);
      await axios.post(`${API_URL}/api/admin/reason-codes`, reasonForm);
      toast.success('Reason code created successfully');
      setReasonDialogOpen(false);
      setReasonForm({ code: '', description: '', category: 'approval' });
      fetchData();
    } catch (error) {
      console.error('Failed to create reason code:', error);
      toast.error('Failed to create reason code');
    } finally {
      setSubmittingReason(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      approval: 'bg-emerald-100 text-emerald-800',
      rejection: 'bg-red-100 text-red-800',
      more_info: 'bg-blue-100 text-blue-800',
      fraud: 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-slate-100 text-slate-800';
  };

  // API Key handlers
  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    try {
      setCreatingKey(true);
      const response = await axios.post(`${API_URL}/api/admin/api-keys`, { name: newKeyName });
      setNewlyCreatedKey(response.data);
      setShowNewKey(true);
      setNewKeyName('');
      fetchData();
      toast.success('API key created successfully');
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error('Failed to create API key');
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin/api-keys/${keyId}`);
      toast.success('API key revoked');
      fetchData();
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Admin Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure policies, thresholds, and reason codes
          </p>
        </div>
        <div className="flex gap-2">
          <PageHelp pageKey="settings" />
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="policies" className="w-full">
        <TabsList>
          <TabsTrigger value="policies" className="gap-2">
            <Globe className="h-4 w-4" />
            Country Policies
          </TabsTrigger>
          <TabsTrigger value="reasons" className="gap-2">
            <Tag className="h-4 w-4" />
            Reason Codes
          </TabsTrigger>
          <TabsTrigger value="api" className="gap-2">
            <Webhook className="h-4 w-4" />
            API Integration
          </TabsTrigger>
          <TabsTrigger value="general" className="gap-2">
            <SettingsIcon className="h-4 w-4" />
            General
          </TabsTrigger>
        </TabsList>

        {/* Country Policies Tab */}
        <TabsContent value="policies" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Dialog open={policyDialogOpen} onOpenChange={(open) => {
              setPolicyDialogOpen(open);
              if (!open) resetPolicyForm();
            }}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-policy-btn" onClick={resetPolicyForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Policy
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingPolicy ? 'Edit Country Policy' : 'Create Country Policy'}</DialogTitle>
                  <DialogDescription>
                    {editingPolicy ? 'Update verification rules for this country' : 'Define verification rules for a specific country'}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Country Code</Label>
                      <Input 
                        placeholder="e.g., NG"
                        value={policyForm.country_code}
                        onChange={(e) => setPolicyForm({ ...policyForm, country_code: e.target.value.toUpperCase() })}
                        disabled={!!editingPolicy}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Country Name</Label>
                      <Input 
                        placeholder="e.g., Nigeria"
                        value={policyForm.country_name}
                        onChange={(e) => setPolicyForm({ ...policyForm, country_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SLA Hours</Label>
                      <Input 
                        type="number"
                        value={policyForm.sla_hours}
                        onChange={(e) => setPolicyForm({ ...policyForm, sla_hours: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Retries</Label>
                      <Input 
                        type="number"
                        value={policyForm.max_retries}
                        onChange={(e) => setPolicyForm({ ...policyForm, max_retries: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Auto-Approve Threshold (%)</Label>
                      <Input 
                        type="number"
                        value={policyForm.auto_approve_threshold}
                        onChange={(e) => setPolicyForm({ ...policyForm, auto_approve_threshold: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Auto-Reject Threshold (%)</Label>
                      <Input 
                        type="number"
                        value={policyForm.auto_reject_threshold}
                        onChange={(e) => setPolicyForm({ ...policyForm, auto_reject_threshold: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => { setPolicyDialogOpen(false); resetPolicyForm(); }}>Cancel</Button>
                  <Button onClick={handleCreatePolicy} disabled={submittingPolicy}>
                    {submittingPolicy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {editingPolicy ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : policies.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead>SLA Hours</TableHead>
                      <TableHead>Auto-Approve</TableHead>
                      <TableHead>Auto-Reject</TableHead>
                      <TableHead>Max Retries</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((policy) => (
                      <TableRow key={policy.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{policy.country_name}</p>
                            <p className="text-sm text-muted-foreground">{policy.country_code}</p>
                          </div>
                        </TableCell>
                        <TableCell>{policy.sla_hours}h</TableCell>
                        <TableCell>{policy.auto_approve_threshold}%</TableCell>
                        <TableCell>{policy.auto_reject_threshold}%</TableCell>
                        <TableCell>{policy.max_retries}</TableCell>
                        <TableCell>
                          <Badge variant={policy.is_active ? "default" : "secondary"}>
                            {policy.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditPolicy(policy)}
                            data-testid={`edit-policy-${policy.country_code}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <Globe className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-lg font-medium">No policies configured</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add country policies to define verification rules
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reason Codes Tab */}
        <TabsContent value="reasons" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Dialog open={reasonDialogOpen} onOpenChange={setReasonDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-reason-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Reason Code
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Reason Code</DialogTitle>
                  <DialogDescription>
                    Define a new reason code for case decisions
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Code</Label>
                    <Input 
                      placeholder="e.g., DOC_EXPIRED"
                      value={reasonForm.code}
                      onChange={(e) => setReasonForm({ ...reasonForm, code: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input 
                      placeholder="e.g., Document has expired"
                      value={reasonForm.description}
                      onChange={(e) => setReasonForm({ ...reasonForm, description: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={reasonForm.category}
                      onChange={(e) => setReasonForm({ ...reasonForm, category: e.target.value })}
                    >
                      <option value="approval">Approval</option>
                      <option value="rejection">Rejection</option>
                      <option value="more_info">More Info</option>
                      <option value="fraud">Fraud</option>
                    </select>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setReasonDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreateReasonCode} disabled={submittingReason}>
                    {submittingReason && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : reasonCodes.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reasonCodes.map((code) => (
                      <TableRow key={code.id}>
                        <TableCell className="font-mono font-medium">{code.code}</TableCell>
                        <TableCell>{code.description}</TableCell>
                        <TableCell>
                          <Badge className={getCategoryColor(code.category)}>
                            {code.category.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={code.is_active ? "default" : "secondary"}>
                            {code.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <Tag className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-lg font-medium">No reason codes</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add reason codes for case decisions
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Integration Tab */}
        <TabsContent value="api" className="mt-6 space-y-6">
          {/* API Keys Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5" />
                    API Keys
                  </CardTitle>
                  <CardDescription>
                    Manage API keys for external system integrations
                  </CardDescription>
                </div>
                <Dialog open={apiKeyDialogOpen} onOpenChange={(open) => {
                  setApiKeyDialogOpen(open);
                  if (!open) {
                    setNewlyCreatedKey(null);
                    setShowNewKey(false);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="create-api-key-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Create API Key
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create API Key</DialogTitle>
                      <DialogDescription>
                        Create a new API key for webhook access
                      </DialogDescription>
                    </DialogHeader>
                    
                    {!newlyCreatedKey ? (
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Key Name</Label>
                          <Input 
                            placeholder="e.g., Production Integration"
                            value={newKeyName}
                            onChange={(e) => setNewKeyName(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            Give this key a descriptive name to identify its purpose
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4 py-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-start gap-2">
                            <Key className="h-5 w-5 text-amber-600 mt-0.5" />
                            <div>
                              <p className="font-medium text-amber-800">Save this key now!</p>
                              <p className="text-sm text-amber-700">
                                This is the only time the full key will be displayed. Copy and store it securely.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Your API Key</Label>
                          <div className="flex gap-2">
                            <Input 
                              type={showNewKey ? "text" : "password"}
                              value={newlyCreatedKey.key}
                              readOnly
                              className="font-mono text-sm"
                            />
                            <Button variant="outline" size="icon" onClick={() => setShowNewKey(!showNewKey)}>
                              {showNewKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button variant="outline" size="icon" onClick={() => copyToClipboard(newlyCreatedKey.key)}>
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <DialogFooter>
                      {!newlyCreatedKey ? (
                        <Button onClick={handleCreateApiKey} disabled={creatingKey}>
                          {creatingKey && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Create Key
                        </Button>
                      ) : (
                        <Button onClick={() => {
                          setApiKeyDialogOpen(false);
                          setNewlyCreatedKey(null);
                          setShowNewKey(false);
                        }}>
                          Done
                        </Button>
                      )}
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {apiKeys.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Key className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No API keys created yet</p>
                  <p className="text-sm">Create an API key to enable external integrations</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Key</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Last Used</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apiKeys.map((key) => (
                      <TableRow key={key.id}>
                        <TableCell className="font-medium">{key.name}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {key.key_prefix}...
                          </code>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(key.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {key.last_used ? new Date(key.last_used).toLocaleDateString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={key.is_active ? 'default' : 'secondary'}>
                            {key.is_active ? 'Active' : 'Revoked'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {key.is_active && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleRevokeApiKey(key.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* API Documentation Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                Webhook API Documentation
              </CardTitle>
              <CardDescription>
                Use these endpoints to integrate EdoVerify with your systems
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Endpoint Overview */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Webhook className="h-4 w-4" />
                  Create Invitation Endpoint
                </h4>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-800">POST</Badge>
                  <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                    {API_URL}/api/webhooks/invite
                  </code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${API_URL}/api/webhooks/invite`)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Call this endpoint to automatically send verification invitations to candidates from your external systems.
                </p>
              </div>

              {/* Authentication */}
              <div className="space-y-3">
                <h4 className="font-semibold">Authentication</h4>
                <p className="text-sm text-muted-foreground">Include your API key using one of these methods:</p>
                <div className="grid gap-2">
                  <div className="bg-slate-50 rounded p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Query Parameter</p>
                    <code className="text-sm">{API_URL}/api/webhooks/invite?api_key=YOUR_API_KEY</code>
                  </div>
                  <div className="bg-slate-50 rounded p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Authorization Header</p>
                    <code className="text-sm">Authorization: Bearer YOUR_API_KEY</code>
                  </div>
                </div>
              </div>

              {/* Request Body */}
              <div className="space-y-3">
                <h4 className="font-semibold">Request Body</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-emerald-700 mb-2">Required Fields</p>
                    <div className="space-y-2">
                      <div className="bg-white border rounded p-2">
                        <code className="text-sm font-medium">candidate_name</code>
                        <p className="text-xs text-muted-foreground">Full name of the candidate</p>
                      </div>
                      <div className="bg-white border rounded p-2">
                        <code className="text-sm font-medium">candidate_email</code>
                        <p className="text-xs text-muted-foreground">Email address for verification link</p>
                      </div>
                      <div className="bg-white border rounded p-2">
                        <code className="text-sm font-medium">country_code</code>
                        <p className="text-xs text-muted-foreground">ISO code: KE, NG, ZA, NL, etc.</p>
                      </div>
                      <div className="bg-white border rounded p-2">
                        <code className="text-sm font-medium">referrer</code>
                        <p className="text-xs text-muted-foreground">Referrer ID from whitelist below</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-700 mb-2">Optional Fields</p>
                    <div className="space-y-2">
                      <div className="bg-white border rounded p-2">
                        <code className="text-sm font-medium">candidate_phone</code>
                        <p className="text-xs text-muted-foreground">E.164 format: +254712345678</p>
                      </div>
                      <div className="bg-white border rounded p-2 border-blue-300 bg-blue-50">
                        <code className="text-sm font-medium text-blue-700">preferred_contact_method</code>
                        <p className="text-xs text-muted-foreground">email (default), sms, <strong>email_sms</strong> (sends both)</p>
                      </div>
                      <div className="bg-white border rounded p-2">
                        <code className="text-sm font-medium">external_reference</code>
                        <p className="text-xs text-muted-foreground">Your system&apos;s reference ID</p>
                      </div>
                      <div className="bg-white border rounded p-2">
                        <code className="text-sm font-medium">notification_channel</code>
                        <p className="text-xs text-muted-foreground">email, sms, email_sms</p>
                      </div>
                      <div className="bg-white border rounded p-2 border-emerald-300 bg-emerald-50">
                        <code className="text-sm font-medium text-emerald-700">callback_url</code>
                        <p className="text-xs text-muted-foreground">URL to receive verification results (POST)</p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Note: SMS and email_sms require <code>candidate_phone</code> to be provided
                </p>
              </div>

              {/* Example Request */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">Example Request</h4>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(`curl -X POST "${API_URL}/api/webhooks/invite" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "candidate_name": "John Doe",
    "candidate_email": "john.doe@example.com",
    "country_code": "KE",
    "referrer": "${referrerWhitelist[0]?.referrer_id || 'your_referrer_id'}",
    "preferred_contact_method": "email",
    "external_reference": "EMP-12345",
    "callback_url": "https://your-server.com/webhook"
  }'`)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
                <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-sm overflow-x-auto">
{`curl -X POST "${API_URL}/api/webhooks/invite" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "candidate_name": "John Doe",
    "candidate_email": "john.doe@example.com",
    "country_code": "KE",
    "referrer": "${referrerWhitelist[0]?.referrer_id || 'your_referrer_id'}",
    "preferred_contact_method": "email",
    "external_reference": "EMP-12345",
    "callback_url": "https://your-server.com/webhook"
  }'`}
                </pre>
              </div>

              {/* Example Response */}
              <div className="space-y-3">
                <h4 className="font-semibold">Example Response</h4>
                <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-sm overflow-x-auto">
{`{
  "success": true,
  "invitation_id": "inv_abc123",
  "verification_url": "${API_URL}/verify/token_xyz",
  "token": "token_xyz",
  "status": "sent",
  "message": "Invitation sent successfully via email",
  "external_reference": "EMP-12345"
}`}
                </pre>
              </div>

              {/* Status Values */}
              <div className="space-y-3">
                <h4 className="font-semibold">Status Values</h4>
                <div className="grid gap-2 md:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium mb-2">Invitation Status</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">pending</Badge>
                      <Badge variant="outline" className="bg-blue-50">sent</Badge>
                      <Badge variant="outline" className="bg-amber-50">opened</Badge>
                      <Badge variant="outline" className="bg-emerald-50">submitted</Badge>
                      <Badge variant="outline" className="bg-slate-50">expired</Badge>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">Case Status</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">new</Badge>
                      <Badge variant="outline" className="bg-blue-50">in_review</Badge>
                      <Badge variant="outline" className="bg-emerald-50">approved</Badge>
                      <Badge variant="outline" className="bg-red-50">rejected</Badge>
                      <Badge variant="outline" className="bg-orange-50">fraud_flagged</Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Callback Webhook Documentation */}
              <div className="space-y-3 border-t pt-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Webhook className="h-4 w-4" />
                  Callback Webhook (Outbound)
                </h4>
                <p className="text-sm text-muted-foreground">
                  When you include a <code className="bg-slate-100 px-1 rounded">callback_url</code> in your invitation request, 
                  EdoVerify will POST the verification result to that URL when the case is approved, rejected, or flagged.
                </p>
                
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium text-emerald-800">Callback Payload Example</p>
                  <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs overflow-x-auto">
{`{
  "event": "verification_completed",
  "timestamp": "2026-01-28T15:31:54.252711+00:00",
  "data": {
    "invitation_id": "inv_abc123",
    "case_id": "case_xyz789",
    "external_reference": "EMP-12345",
    "status": "approved",
    "verification_score": 85.5,
    "risk_score": 15,
    "risk_level": "LOW",
    "face_match_score": 92,
    "document_type": "national_id",
    "country": "KE",
    "decision": {
      "status": "approved",
      "reason_codes": ["face_match_verified", "document_valid"],
      "decided_at": "2026-01-28T15:31:53+00:00"
    }
  }
}`}
                  </pre>
                </div>
                
                <div className="grid gap-2 md:grid-cols-2 text-sm">
                  <div className="bg-white border rounded p-3">
                    <p className="font-medium mb-1">Headers Included</p>
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded block">X-EdoVerify-Event: verification_completed</code>
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded block mt-1">Content-Type: application/json</code>
                  </div>
                  <div className="bg-white border rounded p-3">
                    <p className="font-medium mb-1">Response</p>
                    <p className="text-muted-foreground text-xs">
                      Your endpoint should return a 2xx status code. Failures are logged but not retried.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Referrer Whitelist Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5" />
                    Referrer Whitelist
                  </CardTitle>
                  <CardDescription>
                    Only whitelisted referrers can create invitations via the webhook API
                  </CardDescription>
                </div>
                <Dialog open={referrerDialogOpen} onOpenChange={(open) => {
                  setReferrerDialogOpen(open);
                  if (!open) resetReferrerForm();
                }}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-referrer-btn" onClick={resetReferrerForm}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Referrer
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingReferrer ? 'Edit Referrer' : 'Add Referrer to Whitelist'}</DialogTitle>
                      <DialogDescription>
                        {editingReferrer 
                          ? 'Update the referrer details'
                          : 'Add a new allowed referrer for the webhook API'}
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Referrer ID *</Label>
                        <Input 
                          placeholder="e.g., edomatch_app, partner_xyz"
                          value={referrerForm.referrer_id}
                          onChange={(e) => setReferrerForm({ ...referrerForm, referrer_id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_') })}
                          disabled={!!editingReferrer}
                        />
                        <p className="text-xs text-muted-foreground">
                          Unique identifier used in API requests. Lowercase letters, numbers, underscores, and hyphens only.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Display Name *</Label>
                        <Input 
                          placeholder="e.g., Edomatch Main App"
                          value={referrerForm.display_name}
                          onChange={(e) => setReferrerForm({ ...referrerForm, display_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input 
                          placeholder="Optional description for this referrer"
                          value={referrerForm.description}
                          onChange={(e) => setReferrerForm({ ...referrerForm, description: e.target.value })}
                        />
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => { setReferrerDialogOpen(false); resetReferrerForm(); }}>Cancel</Button>
                      <Button onClick={handleSaveReferrer} disabled={submittingReferrer}>
                        {submittingReferrer && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {editingReferrer ? 'Update' : 'Add'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {referrerWhitelist.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Referrer ID</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrerWhitelist.map((referrer) => (
                      <TableRow key={referrer.id || referrer.referrer_id}>
                        <TableCell className="font-mono font-medium">{referrer.referrer_id}</TableCell>
                        <TableCell>{referrer.display_name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {referrer.description || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={referrer.is_active ? "default" : "secondary"}
                            className="cursor-pointer"
                            onClick={() => handleToggleReferrer(referrer.referrer_id)}
                          >
                            {referrer.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {referrer.created_at ? new Date(referrer.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditReferrer(referrer)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteReferrer(referrer.referrer_id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Key className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No referrers configured</p>
                  <p className="text-sm">Add referrers to allow external systems to create invitations</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* General Settings Tab - Moved to end */}
        <TabsContent value="general" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Data Retention</CardTitle>
              <CardDescription>Configure how long data is retained in the system</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Raw Evidence Retention</p>
                  <p className="text-sm text-muted-foreground">Days to retain original evidence files</p>
                </div>
                <Input type="number" className="w-24" defaultValue="365" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Audit Log Retention</p>
                  <p className="text-sm text-muted-foreground">Years to retain audit trail</p>
                </div>
                <Input type="number" className="w-24" defaultValue="7" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>Configure security-related options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enforce 2FA for All Users</p>
                  <p className="text-sm text-muted-foreground">Require two-factor authentication</p>
                </div>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Evidence Download Logging</p>
                  <p className="text-sm text-muted-foreground">Log all evidence download attempts</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Settings;
